import * as React from "react";
import { makeStyles, Spinner, Text, Button } from "@fluentui/react-components";
import { SignOut24Regular } from "@fluentui/react-icons";

export type ConnectionStatus = "loading" | "connected" | "disconnected" | "error";

interface StatusBarProps {
  status: ConnectionStatus;
  workspaceName?: string;
  errorMessage?: string;
  onDisconnect: () => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    padding: "12px 16px",
    marginBottom: "16px",
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  dotConnected: {
    backgroundColor: "#10b981",
  },
  dotDisconnected: {
    backgroundColor: "#9ca3af",
  },
  dotError: {
    backgroundColor: "#ef4444",
  },
});

const StatusBar: React.FC<StatusBarProps> = ({ status, workspaceName, errorMessage, onDisconnect }) => {
  const styles = useStyles();

  if (status === "loading") {
    return (
      <div className={styles.container}>
        <div className={styles.statusRow}>
          <Spinner size="tiny" />
          <Text size={200}>Checking connection...</Text>
        </div>
      </div>
    );
  }

  if (status === "connected") {
    return (
      <div className={styles.container}>
        <div className={styles.statusRow}>
          <span className={`${styles.dot} ${styles.dotConnected}`} />
          <Text size={200} weight="semibold">
            {workspaceName || "Connected"}
          </Text>
        </div>
        <Button
          size="small"
          appearance="subtle"
          icon={<SignOut24Regular />}
          onClick={onDisconnect}
          aria-label="Disconnect"
        />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.container}>
        <div className={styles.statusRow}>
          <span className={`${styles.dot} ${styles.dotError}`} />
          <Text size={200} style={{ color: "#ef4444" }}>
            {errorMessage || "Connection error"}
          </Text>
        </div>
      </div>
    );
  }

  // disconnected
  return (
    <div className={styles.container}>
      <div className={styles.statusRow}>
        <span className={`${styles.dot} ${styles.dotDisconnected}`} />
        <Text size={200} style={{ color: "#6b7280" }}>
          Not connected
        </Text>
      </div>
    </div>
  );
};

export default StatusBar;
